/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { EditableComponent, ResponsiveGrid } from '@adobe/aem-react-editable-components';
import { respGridUtil } from '@marriott/mi-headless-utils';
import { usePersistentGlobalStore, useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { FEATURE_FLAGS, FLAG_TYPE } from '@marriott/mi-feature-flag-utils';
import { eventUtil, canUseDOM } from '@marriott/mi-ui-library';
import { IHQVWrapperProps } from './HQVWrapper.types';
import { PageonModal, ErrorHandlerAlert } from '../../molecules';
import { useCheckBreakpoint } from '../../hooks/lib/useCheckBreakpoint';
import { useHQVRetryOrchestration } from '../../hooks/lib/useHQVRetryOrchestration';
import { useHQVPageModel } from '../../hooks/lib/useHQVPageModel';
import { getSessionIDFromCookie } from '../../utils/src/helper';
import { PROPERTY_INFO_DATA } from '../../modules/constants';
import { useSearchResultsStore } from '../../modules/store/searchResultsStore';
import { useHQVDatalayerTrackingStore } from '../../modules/store/hqvDatalayerTrackingStore';
import { MAP_VIEW_SESSION_KEY } from '../../modules/store/store.constants';
import { useConditionalPageContext } from '../../hooks/lib/useConditionalPageContext';
import { useConditionalHQVContext } from '../../hooks/lib/useConditionalHQVContext';
import { callDataLayerForHQVSessionIdFallback } from '../../modules/utils/searchFormHelper';
import { constants, HQV_SESSION_ID_FAILURE, hqvLog } from '../../constants';
import { hqvInitialState, HQV_ACTIONS } from '../../reducers/HQV';
import { useHQVStore } from '../../modules/store/hqvZustandStore';
import { useHQVPropertyInfo } from '../../hooks/lib/useHQVPropertyInfo';
import { useHQVPhotogallery } from '../../hooks/lib/useHQVPhotogallery';
import { HQVModalFlagContext } from '../../context/HQVModalFlagContext';

const { ROUTING_CONFIG, EXPERIENCE_SEGMENT, HQV_SEOURL, ITEM_PATH_ROOT, PAGE_NAME, SPA_APP_NAME } = constants;

const HQVWrapperContainer: FC<IHQVWrapperProps> = props => {
  const {
    isAuthorMode,
    pagePath,
    itemPath,
    cqItemsOrder,
    cqItems,
    currentLocale = 'en-US',
    model,
    enableUseSWR: enableUseSWRProp,
    isAdfPage = false,
  } = props;

  // Determine if SWR mode is enabled - check both direct prop and model property
  // Defaults to false for backwards compatibility
  const enableUseSWR = enableUseSWRProp ?? model?.['enableUseSWR'] ?? false;

  // Page-scoped labels/flags. `isAdfPage` is injected by the renderer's defineComponentMapping so
  // HQVWrapper knows which page it's being rendered on without inspecting the URL or session shape.
  const pageLabel = isAdfPage ? 'ADF' : 'SERP';
  const zustandFlagKey = isAdfPage
    ? FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND
    : FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND;
  const modalFlagContextValue: 'adf' | 'serp' = isAdfPage ? 'adf' : 'serp';

  // Log component mount
  useEffect(() => {
    hqvLog.log('Component mounted', {
      page: pageLabel,
      isAuthorMode,
      pagePath,
      itemPath,
      cqItemsOrder,
      currentLocale,
      enableUseSWR,
      mode: enableUseSWR ? 'SWR' : 'Legacy',
    });
    return () => {
      hqvLog.log('Component unmounted');
    };
  }, []);

  const globalData = usePersistentGlobalStore((state: any) => state.globalData);
  const { envVarsObject } = useClientEnvVarsStore();

  // Use the flag-aware conditional hook so HQVWrapper reads/writes the SAME store the page's
  // other HQV consumers use:
  //   - SERP_ZUSTAND on / ADF_ZUSTAND on -> Zustand (SWR path or Zustand-backed Legacy)
  //   - flag off                          -> React Context (Legacy path shared with PropertyCardV0,
  //                                          hotelCard, NoImgDiv, SearchResults / SearchFormComponent)
  // Reading directly from Zustand here caused a split-brain in the "authored + flag off" case:
  // property cards dispatched to Context via useConditionalHQVContext, but HQVWrapper's own
  // modalOpenState / propertyIDvalue came from Zustand and never saw the write, so the modal
  // never opened. Routing through the same hook eliminates that mismatch.
  //
  // The direct setters (setModalOpenState, setPropertyID) are only defined on the Zustand path;
  // on the Context path we fall back to hqvDispatch. Wrap both patterns in stable helpers so the
  // call sites below stay readable and identical in both routings.
  const {
    hqvState,
    hqvDispatch,
    setModalOpenState: setModalOpenStateFromHook,
    setPropertyID: setPropertyIDFromHook,
  } = useConditionalHQVContext({ flagType: modalFlagContextValue });

  const setModalOpenState = useCallback(
    (isOpen: boolean) => {
      if (setModalOpenStateFromHook) {
        setModalOpenStateFromHook(isOpen);
      } else {
        hqvDispatch({ type: HQV_ACTIONS.MODAL_OPEN_STATE, payload: isOpen });
      }
    },
    [setModalOpenStateFromHook, hqvDispatch]
  );

  const setPropertyID = useCallback(
    (propertyID: string) => {
      if (setPropertyIDFromHook) {
        setPropertyIDFromHook(propertyID);
      } else {
        hqvDispatch({ type: HQV_ACTIONS.PROPERTY_IDENTITY, payload: propertyID });
      }
    },
    [setPropertyIDFromHook, hqvDispatch]
  );

  // Page-aware page context: reads Zustand vs Context based on the page's own flag (ADF flag on ADF,
  // SERP flag on SERP). Called once to satisfy Rules of Hooks; both usages below share the result.
  const pageContextResult = useConditionalPageContext({ flagType: modalFlagContextValue });
  const { resolvedUrl, headersData, datalayerParams } = pageContextResult;
  const pageContext = pageContextResult;

  const resetDatalayerTracking = useHQVDatalayerTrackingStore(state => state.resetStore);
  const setPropertyInfoStatus = useHQVDatalayerTrackingStore(state => state.setPropertyInfoStatus);
  const isViewportM = useCheckBreakpoint('viewportM');
  const setSearchResultsState = useSearchResultsStore((state: any) => state.setSearchResultsState);

  const sessionIdFallbackCalledRef = useRef(false);

  const { sessionToken, isSessionTokenFallbackTriggred } = useMemo(() => {
    let token = '';
    let fallbackTriggered = false;
    if (globalData?.sessionData && (globalData?.sessionData?.data || globalData?.sessionData?.cacheData)) {
      const sessionObj = globalData?.sessionData?.data ? globalData?.sessionData : globalData?.sessionData?.cacheData;
      if (!sessionObj?.data?.sessionToken && !sessionObj?.key) {
        const sessionTokenFromCookie = getSessionIDFromCookie();
        if (sessionTokenFromCookie) {
          token = sessionTokenFromCookie;
          fallbackTriggered = true;
        }
      } else {
        token = sessionObj?.data?.sessionToken || sessionObj?.key || '';
      }
    }
    return { sessionToken: token, isSessionTokenFallbackTriggred: fallbackTriggered };
  }, [globalData?.sessionData]);

  const getCMSTemplateReqBody = useMemo(
    () => ({
      requestType: ROUTING_CONFIG,
      seoUrl: HQV_SEOURL,
      localeKey: currentLocale,
      experienceSegment: EXPERIENCE_SEGMENT,
      applicationName: SPA_APP_NAME,
      sessionToken: sessionToken,
    }),
    [currentLocale, sessionToken]
  );

  const closeModal = useCallback(() => {
    hqvLog.log('Closing modal');
    resetDatalayerTracking();
    setSearchResultsState([PROPERTY_INFO_DATA], {
      [PROPERTY_INFO_DATA]: { [PROPERTY_INFO_DATA]: {} },
    });
    const modalHeaderComp = document.getElementsByClassName('hqv-modal-content-header')[0] as HTMLElement;
    if (modalHeaderComp) modalHeaderComp.style.visibility = 'hidden';
    const loaderComp = document.getElementsByClassName('modal-content-loader')[0] as HTMLElement;
    if (loaderComp) {
      loaderComp.style.visibility = 'visible';
      loaderComp.style.display = 'block';
      if (!isViewportM) {
        loaderComp.style.display = 'flex';
      }
    }
    const modalDataComp = document.getElementsByClassName('hqv-modal-data')[0] as HTMLElement;
    if (modalDataComp) {
      modalDataComp.style.visibility = 'hidden';
      modalDataComp.style.display = 'none';
    }
    // Use Zustand store to update modal state
    setModalOpenState(false);
    // eslint-disable-next-line react-compiler/react-compiler
    document.body.style.height = '100%';
  }, [resetDatalayerTracking, setSearchResultsState, isViewportM, setModalOpenState]);

  function showModalContent() {
    const modalHeaderComp = document.getElementsByClassName('hqv-modal-content-header')[0] as HTMLElement;
    if (modalHeaderComp) modalHeaderComp.style.visibility = 'visible';
    const loaderComp = document.getElementsByClassName('modal-content-loader')[0] as HTMLElement;
    if (loaderComp) {
      loaderComp.style.visibility = 'hidden';
      loaderComp.style.display = 'none';
    }
    const modalDataComp = document.getElementsByClassName('hqv-modal-data')[0] as HTMLElement;
    if (modalDataComp) {
      modalDataComp.style.visibility = 'visible';
      modalDataComp.style.display = 'block';
    }
    const modalCloseButton = document.getElementsByClassName('popup-close')[0] as HTMLElement;
    modalCloseButton?.focus();
  }

  // Extract propertyId from session data or URL on component mount
  // Use a ref to track if we've already extracted the propertyId to avoid duplicate processing
  const propertyIdExtractedRef = useRef(false);

  useEffect(() => {
    // Skip if we've already successfully extracted a propertyId
    if (propertyIdExtractedRef.current) {
      return;
    }

    // Check both sessionData structures: cacheData.data (Search Results) and data (ADF)
    const sessionDataObj = globalData?.sessionData?.cacheData?.data || globalData?.sessionData?.data || {};
    const { AriesSearch, AriesReservation } = sessionDataObj;
    let propertyId = AriesSearch?.searchCriteria?.propertyId || AriesReservation?.propertyId;

    hqvLog.log('Extracting propertyId from session/URL', {
      fromSession: propertyId,
      fromCacheData: globalData?.sessionData?.cacheData?.data?.AriesReservation?.propertyId,
      fromData: globalData?.sessionData?.data?.AriesReservation?.propertyId,
      currentPropertyId: hqvState.propertyIDvalue,
    });

    // Fallback to URL params if not found in session
    if (!propertyId || propertyId === 'undefined' || propertyId === 'null') {
      try {
        if (canUseDOM) {
          const urlParams = new URLSearchParams(window.location.search);
          // Check both propertyCode (common) and marshaCode (ADF)
          const propertyCode = urlParams.get('propertyCode') || urlParams.get('marshaCode');
          if (propertyCode) {
            propertyId = propertyCode;
            hqvLog.log('PropertyId from URL param:', propertyCode);
          }
        }
      } catch (error) {
        // Silently handle URL parsing errors
      }
    }

    if (propertyId && propertyId !== 'undefined' && propertyId !== 'null') {
      hqvLog.log('Setting propertyId:', propertyId);
      setPropertyID(propertyId);
      propertyIdExtractedRef.current = true;
    }
  }, [globalData?.sessionData?.cacheData?.data, globalData?.sessionData?.data, setPropertyID]);

  useEffect(() => {
    hqvLog.log('Registering showHotelDetailModal event listener');
    eventUtil.on('showHotelDetailModal', () => {
      hqvLog.log('Property clicked - showHotelDetailModal event received');

      // Re-extract propertyId when modal opens (in case it wasn't available on mount)
      // This is especially important for ADF where the property ID might be set later
      if (!hqvState.propertyIDvalue) {
        const sessionDataObj = globalData?.sessionData?.cacheData?.data || globalData?.sessionData?.data || {};
        const { AriesSearch, AriesReservation } = sessionDataObj;
        let propertyId = AriesSearch?.searchCriteria?.propertyId || AriesReservation?.propertyId;

        // Fallback to URL params
        if (!propertyId || propertyId === 'undefined' || propertyId === 'null') {
          try {
            if (canUseDOM) {
              const urlParams = new URLSearchParams(window.location.search);
              propertyId = urlParams.get('propertyCode') || urlParams.get('marshaCode') || '';
            }
          } catch {
            // Silently handle URL parsing errors
          }
        }

        if (propertyId && propertyId !== 'undefined' && propertyId !== 'null') {
          hqvLog.log('Setting propertyId on modal open:', propertyId);
          setPropertyID(propertyId);
        } else {
          hqvLog.log('No propertyId found on modal open', {
            fromCacheData: globalData?.sessionData?.cacheData?.data?.AriesReservation?.propertyId,
            fromData: globalData?.sessionData?.data?.AriesReservation?.propertyId,
            fromURL: canUseDOM ? new URLSearchParams(window.location.search).get('propertyCode') : null,
          });
        }
      }

      resetDatalayerTracking();
      setModalOpenState(true);
    });
  }, [resetDatalayerTracking, setModalOpenState, globalData?.sessionData, hqvState.propertyIDvalue, setPropertyID]);

  // Check if HQV functionality should be enabled based on environment variables
  const isHQVEnabled =
    !!envVarsObject?.['NEXT_PUBLIC_DYNAMIC_ROUTING_URL_CLIENT'] && !!envVarsObject?.['NEXT_PUBLIC_AEM_PATH'];

  // Log HQV enabled status + effective mode/store.
  // `enableUseSWR` here is the EFFECTIVE value passed down from HQVWrapperWithInit
  // (authored enableUseSWR AND the page's own Zustand flag). When the flag is OFF, HQVWrapperWithInit
  // already downgraded us to Legacy, so this log reflects the mode that is actually running.
  // The flag key logged is page-scoped: ADF_ZUSTAND when on ADF, SERP_ZUSTAND when on SERP.
  // (See HQVWrapperWithInit for the authored-vs-effective distinction and the misconfig warn.)
  useEffect(() => {
    const featureFlags = envVarsObject?.['featureFlags'] as unknown as Record<string, FLAG_TYPE> | undefined;
    // Skip logging until the client env store has actually been populated to avoid
    // reporting the flag as false on first render and confusing debug consumers.
    if (!featureFlags || Object.keys(featureFlags).length === 0) return;
    const pageZustandOn = featureFlags[zustandFlagKey] ?? false;
    const effectiveMode = enableUseSWR ? 'SWR' : 'Legacy';
    const store = pageZustandOn ? 'Zustand' : 'Context';

    hqvLog.log('[HQVWrapper] HQV routing:', {
      page: pageLabel,
      isHQVEnabled,
      NEXT_PUBLIC_DYNAMIC_ROUTING_URL_CLIENT: !!envVarsObject?.['NEXT_PUBLIC_DYNAMIC_ROUTING_URL_CLIENT'],
      NEXT_PUBLIC_AEM_PATH: !!envVarsObject?.['NEXT_PUBLIC_AEM_PATH'],
      enableUseSWR,
      [zustandFlagKey]: pageZustandOn,
      effectiveMode,
      store,
    });
  }, [isHQVEnabled, enableUseSWR, envVarsObject, pageLabel, zustandFlagKey]);

  // ============================================================================
  // SWR MODE: Pre-fetch AEM page model on page load using SWR
  // Only active when enableUseSWR is true
  // ============================================================================
  const shouldUseSWRPageModel = enableUseSWR && isHQVEnabled;
  const {
    pageModel: prefetchedPageModel,
    responsiveGridModel: prefetchedGridModel,
    responsiveGridModelProps: prefetchedGridProps,
    isLoading: isPageModelLoading,
  } = useHQVPageModel(
    shouldUseSWRPageModel ? envVarsObject['NEXT_PUBLIC_DYNAMIC_ROUTING_URL_CLIENT'] : null,
    shouldUseSWRPageModel ? getCMSTemplateReqBody : null,
    ITEM_PATH_ROOT,
    PAGE_NAME
  );

  // Log getCMSTemplate (page model) fetch status - only in SWR mode
  useEffect(() => {
    if (enableUseSWR && isHQVEnabled) {
      hqvLog.log('[SWR Mode] getCMSTemplate (Page Model) status:', {
        isLoading: isPageModelLoading,
        hasPageModel: !!prefetchedPageModel,
        hasGridModel: !!prefetchedGridModel,
        hasGridProps: !!prefetchedGridProps,
      });
      if (prefetchedPageModel && !isPageModelLoading) {
        hqvLog.log('[SWR Mode] getCMSTemplate loaded successfully');
      }
    }
  }, [enableUseSWR, isHQVEnabled, isPageModelLoading, prefetchedPageModel, prefetchedGridModel, prefetchedGridProps]);

  // Determine if we should fetch HQV data
  // In both SWR and Legacy modes, we fetch when modal is open and propertyId is available
  const propertyId = hqvState.propertyIDvalue;
  const shouldFetchData = isHQVEnabled && hqvState.modalOpenState && !!propertyId;

  // Log modal state changes
  useEffect(() => {
    hqvLog.log('Modal state changed:', {
      modalOpenState: hqvState.modalOpenState,
      propertyId,
      shouldFetch: shouldFetchData,
      mode: enableUseSWR ? 'SWR' : 'Legacy',
    });
  }, [hqvState.modalOpenState, propertyId, shouldFetchData, enableUseSWR]);

  // ============================================================================
  // DATA FETCHING: Call hooks for property info and photogallery
  // The hooks internally handle SWR vs Legacy mode based on enableUseSWR flag
  // ============================================================================
  const {
    propertyInfo,
    isLoading: propertyInfoLoading,
    error: propertyInfoError,
    refetch: refetchPropertyInfo,
  } = useHQVPropertyInfo(shouldFetchData ? propertyId : null);

  const {
    photogallery,
    isLoading: photogalleryLoading,
    error: photogalleryError,
    refetch: refetchPhotogallery,
  } = useHQVPhotogallery(shouldFetchData ? propertyId : null, {
    cachedPropertyMedia: hqvState.cachedPropertyMedia,
  });

  // Log UXL calls status
  useEffect(() => {
    if (shouldFetchData) {
      const modePrefix = enableUseSWR ? '[SWR Mode]' : '[Legacy Mode]';
      hqvLog.log(`${modePrefix} UXL Calls status:`, {
        propertyId,
        propertyInfo: {
          isLoading: propertyInfoLoading,
          hasData: !!propertyInfo,
          hasError: !!propertyInfoError,
        },
        photogallery: {
          isLoading: photogalleryLoading,
          hasData: !!photogallery,
          hasError: !!photogalleryError,
        },
      });

      if (!propertyInfoLoading && propertyInfo) {
        hqvLog.log(`${modePrefix} PropertyInfo UXL call completed`);
      }
      if (!photogalleryLoading && photogallery) {
        hqvLog.log(`${modePrefix} Photogallery UXL call completed`);
      }
      if (propertyInfoError) {
        hqvLog.error(`${modePrefix} PropertyInfo UXL call failed:`, propertyInfoError);
      }
      if (photogalleryError) {
        hqvLog.error(`${modePrefix} Photogallery UXL call failed:`, photogalleryError);
      }
    }
  }, [
    enableUseSWR,
    shouldFetchData,
    propertyId,
    propertyInfoLoading,
    propertyInfo,
    propertyInfoError,
    photogalleryLoading,
    photogallery,
    photogalleryError,
  ]);

  // Populate the searchResultsStore with property info data for child components (BOTH MODES)
  useEffect(() => {
    if (propertyInfo && !propertyInfoLoading) {
      const modePrefix = enableUseSWR ? '[SWR Mode]' : '[Legacy Mode]';
      hqvLog.log(`${modePrefix} Populating searchResultsStore with property info data`);
      setSearchResultsState(['propertyInfoData'], {
        propertyInfoData: { propertyInfoData: propertyInfo },
      });
    }
  }, [enableUseSWR, propertyInfo, propertyInfoLoading, setSearchResultsState]);

  // Update propertyInfoStatus in the store based on results (BOTH MODES)
  useEffect(() => {
    if (!shouldFetchData || propertyInfoLoading) {
      return;
    }

    const modePrefix = enableUseSWR ? '[SWR Mode]' : '[Legacy Mode]';
    if (propertyInfoError) {
      hqvLog.log(`${modePrefix} Setting propertyInfoStatus to error`);
      setPropertyInfoStatus('error');
    } else if (propertyInfo) {
      hqvLog.log(`${modePrefix} Setting propertyInfoStatus to success`);
      setPropertyInfoStatus('success');
    }
  }, [enableUseSWR, shouldFetchData, propertyInfoLoading, propertyInfo, propertyInfoError, setPropertyInfoStatus]);

  // Pass state to orchestration hook (BOTH MODES now use full state)
  const { hqvFallbackMode } = useHQVRetryOrchestration({
    resolvedUrl: pageContext?.resolvedUrl,
    sessionData: pageContext?.sessionData,
    headersData: pageContext?.headersData,
    query: pageContext?.query,
    propertyId: hqvState.propertyIDvalue,
    brandCode: pageContext?.resolvedUrl?.split('brandCode=')[1]?.split('&')[0],
    datalayerParams: pageContext?.datalayerParams,
    datalayerProperties: pageContext?.datalayerProperties,
    currentLocale: currentLocale,
    onCloseModal: closeModal,
    modalOpenState: hqvState.modalOpenState,
    enableUseSWR, // Pass mode flag - SWR mode uses SWR's built-in retry, Legacy mode uses manual retry
    // Data state - passed in both modes
    propertyInfoError,
    photogalleryError,
    propertyInfoData: propertyInfo,
    photogalleryData: photogallery,
    refetchPropertyInfo,
    refetchPhotogallery,
    isPropertyInfoLoading: propertyInfoLoading,
    isPhotogalleryLoading: photogalleryLoading,
  });

  // Handle amenities_only fallback mode (BOTH MODES)
  useEffect(() => {
    if (hqvFallbackMode === 'amenities_only') {
      const modePrefix = enableUseSWR ? '[SWR Mode]' : '[Legacy Mode]';
      hqvLog.log(`${modePrefix} Fallback mode: amenities_only - showing modal content`);
      showModalContent();
    }
  }, [enableUseSWR, hqvFallbackMode]);

  // Show modal content when data loads (BOTH MODES)
  const allDataLoaded =
    shouldFetchData && !propertyInfoLoading && !photogalleryLoading && (!!propertyInfo || !!photogallery);
  const prevAllDataLoadedRef = useRef(false);

  useEffect(() => {
    // Only trigger showModalContent when transitioning from loading to loaded
    if (allDataLoaded && !prevAllDataLoadedRef.current && hqvState.modalOpenState) {
      const modePrefix = enableUseSWR ? '[SWR Mode]' : '[Legacy Mode]';
      hqvLog.log(`${modePrefix} All data loaded - showing modal content`);
      const loaderComp = document.getElementsByClassName('modal-content-loader')[0] as HTMLElement;
      if (loaderComp && loaderComp.style.visibility !== 'hidden') {
        showModalContent();
      }
    }
    prevAllDataLoadedRef.current = allDataLoaded;
  }, [enableUseSWR, allDataLoaded, hqvState.modalOpenState]);

  // Session token fallback datalayer call (both modes)
  useEffect(() => {
    if (isHQVEnabled) {
      if (isSessionTokenFallbackTriggred && !sessionIdFallbackCalledRef.current) {
        sessionIdFallbackCalledRef.current = true;
        callDataLayerForHQVSessionIdFallback(
          resolvedUrl,
          headersData,
          datalayerParams,
          sessionToken,
          currentLocale,
          true,
          HQV_SESSION_ID_FAILURE
        );
      }
    }
  }, [isHQVEnabled]);

  const AuthorComponents = () => {
    return (
      <ResponsiveGrid
        // @ts-expect-error - ResponsiveGrid types don't match but this is the expected usage pattern
        pagePath={pagePath}
        itemPath={itemPath}
        columnCount="12"
        gridClassNames=""
      />
    );
  };

  const PublishComponents = () => {
    const components = cqItemsOrder?.map((cardName: string, index: number) => {
      if (Object?.prototype?.hasOwnProperty?.call(cqItems, cardName)) {
        const card = cqItems?.[cardName] as Record<string, unknown>;
        const gridUtil = respGridUtil({ ...card });
        return (
          <div key={`${cardName}-${index}`} className="hqv-wrapper-component">
            <ResponsiveGrid {...gridUtil} />
          </div>
        );
      }
      return null;
    });
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{components}</>;
  };

  return (
    <>
      {isAuthorMode ? <AuthorComponents /> : <PublishComponents />}
      {isHQVEnabled && (
        <HQVModalFlagContext.Provider value={modalFlagContextValue}>
          <PageonModal
            templateURL={envVarsObject['NEXT_PUBLIC_DYNAMIC_ROUTING_URL_CLIENT']}
            modelReqBody={getCMSTemplateReqBody}
            hotelQuickViewCacheConfig={globalData?.sessionData?.hotelQuickViewCacheConfig}
            modalOpenState={hqvState.modalOpenState}
            closeModal={closeModal}
            operationSignatures={globalData?.sessionData?.operationSignatures}
            enableApolloUtility={false}
            aemPath={envVarsObject['NEXT_PUBLIC_AEM_PATH']}
            itemPath={ITEM_PATH_ROOT}
            pageName={PAGE_NAME}
            ErrorHandlerAlert={ErrorHandlerAlert}
            flexLayout={true}
            // SWR MODE: Pass pre-fetched AEM model data from SWR hook
            // LEGACY MODE: Pass undefined so PageonModal fetches internally
            prefetchedModel={enableUseSWR ? prefetchedPageModel : undefined}
            prefetchedGridModel={enableUseSWR ? prefetchedGridModel : undefined}
            prefetchedGridProps={enableUseSWR ? prefetchedGridProps : undefined}
            isPageModelLoading={enableUseSWR ? isPageModelLoading : undefined}
          />
        </HQVModalFlagContext.Provider>
      )}
    </>
  );
};

const HQVWrapperWithInit: FC<IHQVWrapperProps> = props => {
  const { enableUseSWR: enableUseSWRProp, model, isAdfPage = false } = props;
  // Page-scoped identifiers. `isAdfPage` is injected by the renderer (see defineComponentMapping)
  // so we pick the correct feature flag and page context without inspecting the URL.
  const pageLabel = isAdfPage ? 'ADF' : 'SERP';
  const zustandFlagKey = isAdfPage
    ? FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND
    : FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND;
  const pageContextFlagType: 'adf' | 'serp' = isAdfPage ? 'adf' : 'serp';

  const { currentLocale = 'en-US' } = useConditionalPageContext({ flagType: pageContextFlagType }) || {};

  // Determine if SWR mode is enabled - check both direct prop and model property
  // Defaults to false for backwards compatibility
  const authoredEnableUseSWR = enableUseSWRProp ?? model?.['enableUseSWR'] ?? false;

  // Detect misconfiguration: authored HQV in SWR mode requires the page's Zustand flag ON.
  // When enableUseSWR=true but the flag is OFF, HQV cannot function correctly.
  // Emit a one-time console.warn so ops/devs surface this without needing debug logs.
  const featureFlags = useClientEnvVarsStore(state => state.envVarsObject?.['featureFlags']) as unknown as
    | Record<string, FLAG_TYPE>
    | undefined;

  // Effective SWR mode = authored SWR AND the page's Zustand flag ON.
  // SWR relies on Zustand as the shared store between HQVWrapper (writer) and modal readers
  // (SearchResultsMainComponent on SERP, SearchFormComponent on ADF via useConditionalHQVContextADF),
  // so when the flag is OFF we downgrade to Legacy regardless of the AEM checkbox. This prevents the
  // double-modal bug where HQVWrapper renders its SWR modal while the page's modal owner reads
  // enableUseSWR from Context (never written) and simultaneously renders its legacy PageonModal.
  //
  // Note: before feature flags hydrate, `featureFlags` is undefined. In that window we default
  // `pageZustandOn` to `true` so the correctly-configured path (both on) does not flicker
  // Legacy -> SWR after hydration. In the misconfigured path this means a brief SWR render
  // before we downgrade to Legacy on the next render; acceptable because the misconfig should
  // not exist in production and the console.warn below still fires.
  const flagsHydrated = !!featureFlags && Object.keys(featureFlags).length > 0;
  const pageZustandOn = flagsHydrated ? featureFlags?.[zustandFlagKey] ?? false : true;
  const enableUseSWR = authoredEnableUseSWR && pageZustandOn;

  const hasWarnedFlagMismatchRef = useRef(false);
  useEffect(() => {
    // Wait until the client env store has actually been populated with feature flags.
    // On first render the store can be empty (before _app.tsx setEnvVars hydration completes),
    // so treating "undefined" as "flag off" would produce a false-positive warning.
    if (!flagsHydrated) {
      return;
    }
    if (authoredEnableUseSWR && !pageZustandOn && !hasWarnedFlagMismatchRef.current) {
      hasWarnedFlagMismatchRef.current = true;
      console.warn(
        `[HQVWrapper] Misconfiguration on ${pageLabel}: enableUseSWR=true but ${zustandFlagKey}=false. 
          SWR relies on Zustand as the shared store, so HQV has been downgraded to Legacy mode. 
          Fix: enable ${zustandFlagKey}, or uncheck enableUseSWR on the HQVWrapper AEM component.`,
        { page: pageLabel, authoredEnableUseSWR, [zustandFlagKey]: pageZustandOn }
      );
    }
  }, [authoredEnableUseSWR, pageZustandOn, flagsHydrated, pageLabel, zustandFlagKey]);

  // Track if store has been initialized using a ref guard
  // useLayoutEffect runs synchronously before paint, ensuring store is initialized before children render
  const storeInitializedRef = useRef(false);
  useLayoutEffect(() => {
    if (!storeInitializedRef.current) {
      useHQVStore.getState().hqvInitialization(hqvInitialState);
      useHQVStore.getState().setEnableUseSWR(enableUseSWR);
      hqvLog.log('Store initialized with enableUseSWR:', enableUseSWR);
      storeInitializedRef.current = true;
    }
  }, []);

  // Publish HQVWrapper's presence to Zustand so the page's modal owner
  // (SearchResultsMainComponent on SERP, SearchFormComponent on ADF) can suppress its own legacy
  // PageonModal render. This runs regardless of feature flags because the signal is presence-only
  // and must be readable by the modal owner even when it is routing reads through Context.
  // Cleanup on unmount so a stale "true" doesn't linger for the next page in a client-side
  // navigation (Next.js keeps the Zustand singleton across route changes).
  useLayoutEffect(() => {
    useHQVStore.getState().setIsHQVWrapperMounted(true);
    return () => {
      useHQVStore.getState().setIsHQVWrapperMounted(false);
    };
  }, []);

  // Update enableUseSWR if it changes after initial mount.
  // This also handles the "flags hydrate later" case: if we initially guessed SWR=true and
  // hydration reveals the page flag=false, this effect writes SWR=false to Zustand so the SWR
  // hooks stop firing on the next render.
  useEffect(() => {
    useHQVStore.getState().setEnableUseSWR(enableUseSWR);
  }, [enableUseSWR]);

  // Override the incoming enableUseSWR prop with the effective value so HQVWrapperContainer
  // (which re-derives `enableUseSWR = enableUseSWRProp ?? model?.enableUseSWR ?? false`) sees
  // the downgraded value everywhere it gates on SWR-vs-Legacy behavior. Also forward isAdfPage
  // so the container consistently uses page-scoped labels/flags.
  return (
    <HQVWrapperContainer {...props} enableUseSWR={enableUseSWR} currentLocale={currentLocale} isAdfPage={isAdfPage} />
  );
};

const Config = {
  emptyLabel: 'HQV Wrapper',
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isEmpty: function (props: any) {
    return !props || !props.cqItemsOrder || props.cqItemsOrder.length === 0;
  },
  resourceType: `mi-aem-shop-spa/components/content/hqvwrapper`,
};

export const HQVWrapper: FC<IHQVWrapperProps> = props => (
  <EditableComponent config={Config} {...props}>
    <HQVWrapperWithInit {...props} />
  </EditableComponent>
);

export const HQVWrapperEditable = HQVWrapper;
